/**
 * THE SETTLED SEA: what the boat does once it has learned the whole chart.
 *
 * -- THE RULING THIS FILE EXISTS TO SATISFY ----------------------------------
 *
 * CONTRACT.md section 1.5d, owner, 2026-09-01: "Position can no longer be a raw function
 * of unbounded progression... it is the mapping that must change, and a boat that has
 * learned the whole sea needs a state that reads as AT HOME IN A KNOWN PLACE, never as
 * circling because the world ran out."
 *
 * The file this one replaces, `horizon.ts`, did exactly the forbidden thing and did it
 * for 96.34% of the game. MEASURED by round 3's critic on the production world: the boat
 * left the charted sea at hour 27.19 of a 742-hour lifetime, joined a circle of radius
 * WORLD_RADIUS_LEAGUES - the empty rim of the sea, the farthest water from every charted
 * place - and completed 1,128 laps of it by wall-clock hour 960, with the chart's only
 * readout counting those laps. `horizon.ts` is deleted rather than edited, and
 * `patrolLaps` is deleted rather than renamed, because a lap counter is the exact tell
 * the ruling names.
 *
 * -- WHAT REPLACES IT: THE ROUND ---------------------------------------------
 *
 * A boat that has sailed the whole sea does not leave it. It keeps its rounds.
 *
 *     lies to at the farthest ground   ->   stands homeward, raising every charted place
 *     it passes   ->   lies at home   ->   stands seaward again, raising them in reverse
 *
 * Everything about that state is water the player has already charted, every part of it
 * is named, and both ends of it are places rather than coordinates. The readout past the
 * charted end therefore makes the SAME KIND OF PROMISE as the readout before it - the next
 * place, a bearing and a time - except that the place now has a name, because the sea is
 * known. Nothing counts laps and nothing recedes.
 *
 * -- THE THREE PROPERTIES, AND WHY EACH IS LOAD-BEARING ----------------------
 *
 *   CONTINUOUS IN POSITION AND IN HEADING. The round begins exactly where the charted
 *   track ends, at the same point and the same heading, and closes onto its own start.
 *   The 180-degree come-about is not a jump: it is spread across the hours the boat lies
 *   to at each end, so the heading swings the way a moored boat swings while the position
 *   stands still. `settled.test.ts` walks the seam and both turns at fine steps and bounds
 *   the largest step in each.
 *
 *   THE PACE STOPS ACCELERATING, WHICH IS THE ACTUAL DEFECT UNDER THE LAP COUNT. Track
 *   distance is a raw function of progression and progression accelerates for ever: the
 *   drift MEASURED on the production curve runs 2.99 m/s at hour 1, 4.18 at the charted
 *   end, 7.09 at hour 400 and 8.91 at hour 742. A settled state driven by that number is a
 *   boat that circles faster every week it is played. So the round is paced in MODELLED
 *   HOURS, at exactly the pace the drift had when the sea ran out - `settledLeaguesPerHour`
 *   - which makes the speed continuous across the seam and CONSTANT for ever after. That
 *   is what "at home" means mechanically: a rhythm, not a curve.
 *
 *   STILL A PURE FUNCTION OF PROGRESSION. Hours are `curve.hoursAtProgress(progress)`, so
 *   the same progression always lands on the same point of the round, on any machine,
 *   watched or not. No accumulator, no persisted phase, no lap counter to save.
 *
 * -- THE ONE COST, STATED --------------------------------------------------
 *
 * The settled phase needs the curve's INVERSE, which the charted sea did not. For the
 * simulation curve that is a binary search over a table built lazily to the queried hour:
 * MEASURED 612 ms to grow it to hour 800 once, then ~1 ms per query, cached for the life
 * of the world (sim-bridge.ts memoises worlds by curve id). A save deep enough to be
 * settled pays that once on its first frame.
 *
 * THE BLIND SPOT THIS PARAGRAPH USED TO NAME IS CLOSED, AND IT WAS NOT A BLIND SPOT, IT
 * WAS THE DEFECT. It read: "past the curve's own horizon the inverse clamps, and the round
 * stops." It did. At modelled hour 800.000 - day 34 of once-a-day play - the inverse began
 * returning 800, `since` stopped advancing, and this file's round froze for ever at the
 * farthest ground from home (CL4-C-01). The round was never wrong; the hours it was handed
 * stopped moving. Round 5 made the curve TOTAL in both directions, so there is no horizon
 * past which the inverse stops answering, and src/voyage/deep-time.test.ts asserts the boat
 * still keeps its round out to the simulation's own 2^40-tick clock ceiling.
 */

import type { PlaceCall } from './calls';
import { NO_CALLS, routeCallPlaceIdAt, routeHeadingAt, routePositionAt } from './calls';
import type { Chart } from './chart';
import { VOYAGE_SHAPE } from './constants';
import type { VoyagePacing } from './pacing';
import { chartedSpanModelledHours, driftLeaguesInHour } from './pacing';
import { spiralRadius, thetaAtArcLength } from './track';
import type { Degrees, Leagues, Vec2 } from './units';

/**
 * What the boat is doing. One value, always present, and there is no fifth case.
 *
 * `charted` is the whole of the charted sea; the other three are the round. It replaces
 * round 3's `beyondCharted` boolean plus `patrolLaps` counter: a boolean could not say
 * which way the boat was standing, and the counter said the only thing it must not.
 */
export type Standing = 'charted' | 'lying' | 'homeward' | 'seaward';

export interface SettledSea {
  /** Track leagues from home to the last charted sighting. The end of the charted sea. */
  readonly chartedEndLeagues: Leagues;
  /** Modelled hours of play to reach it, under this world's own curve. */
  readonly chartedSpanHours: number;
  /** Greatest distance from home the boat ever reaches. Finite, and it stops growing. */
  readonly outerRadiusLeagues: Leagues;
  /** THE RHYTHM. Leagues of track per modelled hour, fixed at the drift pace at settling. */
  readonly settledLeaguesPerHour: number;
  /** Hours lying to at each end of the round, while the boat comes about. */
  readonly restHours: number;
  /** Hours under way in one round: out and back over the whole charted track. */
  readonly passageHours: number;
  /** One full round: lie to, stand homeward, lie at home, stand seaward. */
  readonly roundHours: number;
  /** False when the chart has no end to settle at; then the track spirals on. */
  readonly hasChartedEnd: boolean;
}

/** A world with nothing charted. The spiral runs on, exactly as it did in round 1. */
export const OPEN_SEA: SettledSea = {
  chartedEndLeagues: 0,
  chartedSpanHours: 0,
  outerRadiusLeagues: 0,
  settledLeaguesPerHour: 0,
  restHours: 0,
  passageHours: 0,
  roundHours: 0,
  hasChartedEnd: false,
};

function finitePositive(v: number): boolean {
  return Number.isFinite(v) && v > 0;
}

/**
 * Solve the round for a chart and the pace that world was solved against.
 *
 * IT TAKES THE PACING AND NOT ONLY THE CHART, and that is the whole difference from
 * round 3. The old `naturalTrack(chart.lastSightTrackDistance)` was geometry alone, so the
 * only clock it could use was track distance - which is the accelerating quantity. The
 * pace is what lets the round be measured in hours and therefore be steady.
 *
 * DEGENERATE CASE, stated rather than discovered: `driftLeaguesInHour` reads the curve at
 * the charted span, and a curve that pays nothing across that span has no measurable
 * settling pace. Such a world gets `OPEN_SEA` - the spiral, unchanged - rather than a round
 * paced by a zero.
 *
 * IT IS NO LONGER A HORIZON PROBLEM. This paragraph used to say "every curve has a horizon
 * past which its inverse clamps" and point at lifetime.test.ts's `horizon > lifetime` as
 * the fence. That fence compared a horizon against the LEVEL 99 MILESTONE and missed
 * CL4-C-01 by 57.69 hours. Curves are now required to be total (pacing.ts), so the only
 * way into this branch is a curve that genuinely earns nothing, which refuses loudly at
 * the source rather than silently here.
 */
export function settledSea(chart: Chart, pacing: VoyagePacing): SettledSea {
  const chartedEndLeagues = chart.lastSightTrackDistance;
  if (!finitePositive(chartedEndLeagues)) return OPEN_SEA;

  const chartedSpanHours = chartedSpanModelledHours(chart, pacing);
  if (!finitePositive(chartedSpanHours)) return OPEN_SEA;

  const settledLeaguesPerHour = driftLeaguesInHour(pacing, chartedSpanHours);
  if (!finitePositive(settledLeaguesPerHour)) return OPEN_SEA;

  // Out and back over the whole charted track, at the pace the drift had when it ended.
  const passageHours = (2 * chartedEndLeagues) / settledLeaguesPerHour;
  if (!finitePositive(passageHours)) return OPEN_SEA;

  const restHours = VOYAGE_SHAPE.settledRestFraction * passageHours;
  return {
    chartedEndLeagues,
    chartedSpanHours,
    outerRadiusLeagues: spiralRadius(thetaAtArcLength(chartedEndLeagues)),
    settledLeaguesPerHour,
    restHours,
    passageHours,
    roundHours: passageHours + 2 * restHours,
    hasChartedEnd: true,
  };
}

/** True once the natural drift has sailed the whole charted sea and taken up the round. */
export function isSettled(sea: SettledSea, trackDistance: Leagues): boolean {
  return sea.hasChartedEnd && Number.isFinite(trackDistance) && trackDistance > sea.chartedEndLeagues;
}

/**
 * The farthest from home the boat ever gets. Finite whenever there is a charted end, which
 * is what makes every bearing and every ETA on the chart stop growing.
 */
export function settledReachLeagues(sea: SettledSea): Leagues {
  return sea.hasChartedEnd ? sea.outerRadiusLeagues : Infinity;
}

/** Compass degrees, normalised into [0, 360). */
function wrapDeg(d: Degrees): Degrees {
  return ((d % 360) + 360) % 360;
}

/**
 * The come-about, as a fraction of the way through it. The boat always turns the same way,
 * so one full round is one full turn and the heading never doubles back on itself.
 */
function turning(fromDeg: Degrees, u: number): Degrees {
  return wrapDeg(fromDeg + 180 * (u < 0 ? 0 : u > 1 ? 1 : u));
}

/** Where the boat naturally is, which way it is pointing, and what it is doing. */
export interface NaturalFix {
  readonly position: Vec2;
  readonly headingDeg: Degrees;
  readonly standing: Standing;
  /**
   * Leagues along the CHARTED track the boat is over right now. Inside the charted sea it
   * is the raw drift distance; on the round it is where on the outward track the boat has
   * come back to. Never past `chartedEndLeagues`, which is what "bounded" means here.
   */
  readonly overTrackLeagues: Leagues;
  /** Hours into the current round. 0 inside the charted sea. */
  readonly roundPhaseHours: number;
  /**
   * THE PLACE THE BOAT HAS ARRIVED AT, or null. CONTRACT 1.5n(a): arrival is a state, so it is
   * carried on the natural fix beside the position it belongs to, not noticed by a renderer.
   * Set only while the boat is actually made fast - `calls.ts` decides - and null everywhere else,
   * including on the run in and the stand out.
   */
  readonly callingAt: string | null;
}

/**
 * THE MAPPING. Progression in, position out, and the only branch is whether the charted
 * sea is finished.
 *
 * `trackDistance` is the raw monotone drift distance and decides WHICH state the boat is
 * in. `modelledHours` is that same progression read as hours and paces the round. Both are
 * pure functions of the one progression scalar, so this function is too.
 */
export function naturalFixAt(
  sea: SettledSea,
  trackDistance: Leagues,
  modelledHours: number,
  calls: readonly PlaceCall[] = NO_CALLS,
): NaturalFix {
  const d = Number.isFinite(trackDistance) ? Math.max(0, trackDistance) : 0;

  if (!isSettled(sea, d)) {
    return {
      position: routePositionAt(calls, d),
      headingDeg: routeHeadingAt(calls, d),
      standing: 'charted',
      overTrackLeagues: d,
      roundPhaseHours: 0,
      callingAt: routeCallPlaceIdAt(calls, d),
    };
  }

  const end = sea.chartedEndLeagues;
  const hours = Number.isFinite(modelledHours) ? modelledHours : sea.chartedSpanHours;
  const since = Math.max(0, hours - sea.chartedSpanHours);
  const phase = sea.roundHours > 0 ? since % sea.roundHours : 0;
  const half = sea.passageHours / 2;
  const rest = sea.restHours;

  // Lying to at the farthest ground, coming about. The sea is finished; the boat stops.
  if (phase < rest) {
    return {
      position: routePositionAt(calls, end),
      headingDeg: turning(routeHeadingAt(calls, end), rest > 0 ? phase / rest : 1),
      standing: 'lying',
      overTrackLeagues: end,
      roundPhaseHours: phase,
      callingAt: routeCallPlaceIdAt(calls, end),
    };
  }

  // Standing homeward down the whole charted track, raising every place in reverse order.
  if (phase < rest + half) {
    const s = half > 0 ? end * (1 - (phase - rest) / half) : 0;
    return {
      position: routePositionAt(calls, s),
      headingDeg: wrapDeg(routeHeadingAt(calls, s) + 180),
      standing: 'homeward',
      overTrackLeagues: s,
      roundPhaseHours: phase,
      callingAt: routeCallPlaceIdAt(calls, s),
    };
  }

  // Lying at home. Position is exactly the origin, which is exactly the home place.
  if (phase < 2 * rest + half) {
    return {
      position: routePositionAt(calls, 0),
      headingDeg: turning(wrapDeg(routeHeadingAt(calls, 0) + 180), rest > 0 ? (phase - rest - half) / rest : 1),
      standing: 'lying',
      overTrackLeagues: 0,
      roundPhaseHours: phase,
      callingAt: routeCallPlaceIdAt(calls, 0),
    };
  }

  // Standing seaward again, raising every place in chart order.
  const s = half > 0 ? end * ((phase - 2 * rest - half) / half) : end;
  return {
    position: routePositionAt(calls, s),
    headingDeg: routeHeadingAt(calls, s),
    standing: 'seaward',
    overTrackLeagues: s,
    roundPhaseHours: phase,
    callingAt: routeCallPlaceIdAt(calls, s),
  };
}

/**
 * Hours until the boat is next over `targetTrackLeagues` of the charted track.
 *
 * This is what makes the settled readout a promise rather than a description: it is the
 * same question the charted readout answers - when do I raise the next place - asked of a
 * boat that already knows the answer's name. Infinity when the world has no round.
 *
 * IT TAKES THE PHASE, NOT A FIX, so that the only caller can hand it the phase already on
 * the `VoyageFix` instead of solving the natural state a second time. A second solve is a
 * second answer that can disagree with the first, which is the shape this lane keeps
 * paying for.
 */
export function hoursUntilOverTrack(
  sea: SettledSea,
  roundPhaseHours: number,
  targetTrackLeagues: Leagues,
): number {
  if (!sea.hasChartedEnd || !Number.isFinite(targetTrackLeagues)) return Infinity;
  const target = Math.max(0, Math.min(targetTrackLeagues, sea.chartedEndLeagues));
  const pace = sea.settledLeaguesPerHour;
  if (!finitePositive(pace)) return Infinity;

  // Hours from the START of the round to the first moment the boat is over `target`. The
  // round runs: lie at the end, home, lie at home, out again - so a given track distance
  // is passed exactly twice per round, once on each leg.
  const rest = sea.restHours;
  const half = sea.passageHours / 2;
  const homewardAt = rest + (sea.chartedEndLeagues - target) / pace;
  const seawardAt = 2 * rest + half + target / pace;

  const phase = Number.isFinite(roundPhaseHours) ? roundPhaseHours : 0;
  let best = Infinity;
  for (const at of [homewardAt, seawardAt]) {
    const delta = at - phase;
    const wrapped = delta >= 0 ? delta : delta + sea.roundHours;
    if (wrapped < best) best = wrapped;
  }
  return best;
}
